/**
 * 跑馬燈元件
 *
 * 顯示無縫滾動的文字或內容，支援自訂樣式與內容。
 *
 * @component
 * @param {MarqueeProps} props - 元件的屬性。
 * @param {string} [props.classes] - Tailwind CSS 類別，用於自訂背景色、文字顏色、文字大小等樣式。
 * @param {React.ReactNode} [props.children] - 要顯示在 Marquee 元件內的內容。
 *
 * @example
 * <Marquee classes="!bg-red-500 !text-white text-lg">
 *   這是一段滾動的文字
 * </Marquee>
 */
import { useEffect, useRef, useState } from "react";

interface MarqueeProps {
  classes?: string;
  children?: React.ReactNode;
}

const Marquee = ({ classes = "", children }: MarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstElementRef = useRef<HTMLSpanElement>(null);
  const [elementWidth, setElementWidth] = useState<number>(0);
  const [repeatCount, setRepeatCount] = useState<number>(2);

  useEffect(() => {
    const updateWidth = () => {
      if (firstElementRef.current && containerRef.current) {
        const elementRect = firstElementRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const elementWidthValue = elementRect.width;
        const containerWidth = containerRect.width;

        setElementWidth(elementWidthValue);

        // 計算需要多少個重複元素才能避免結尾無縫循環時不留白
        // + 2 - 額外的元素作為緩衝，確保動畫移動結束時區塊內始終有內容
        const minRepeats = Math.ceil(containerWidth / elementWidthValue) + 2;
        setRepeatCount(Math.max(minRepeats, 3));
      }
    };

    // 使用 requestAnimationFrame 確保 DOM 已渲染 (瀏覽器 API，用於在下次重繪前執行回呼函式)
    requestAnimationFrame(() => {
      updateWidth();
    });

    window.addEventListener("resize", updateWidth);

    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, [children]);

  // 計算動畫移動距離：元素寬度 + 32px 間隔
  const animationStyle =
    elementWidth > 0
      ? ({ "--marquee-width": `-${elementWidth + 32}px` } as React.CSSProperties)
      : {};

  const elements = Array.from({ length: repeatCount }, (_, index) => (
    <span
      key={index}
      ref={index === 0 ? firstElementRef : null}
      className="inline-block flex-shrink-0 whitespace-nowrap"
    >
      {children}
    </span>
  ));

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden bg-primary px-4 py-2 text-white ${classes}`}
    >
      <div className="brand-animate-marquee" style={animationStyle}>
        {elements}
      </div>
    </div>
  );
};

export default Marquee;
